/**
 * Address book picker (DESIGN.md T11, like Outlook's "To…" dialog): search the directory and the
 * personal contacts, tick people/groups and add them to To, Cc or Bcc. Opened from the field
 * labels of the composer; the result is applied only on OK.
 */
import { SearchIcon, XIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { Checkbox } from '../components/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/dialog';
import { Input } from '../components/input';
import { Skeleton } from '../components/misc';
import { Tabs, TabsList, TabsTrigger } from '../components/tabs';
import { InfiniteScrollSentinel } from '../composites/infinite-scroll-sentinel';
import { EmptyState, ErrorState } from '../composites/states';
import { useInfiniteList } from '../hooks/use-infinite-list';
import { cn } from '../lib/utils';
import { PersonAvatar } from './person';
import type { PersonEntry, RecipientSource } from './recipients';

export type AddressBookFilter = 'all' | 'gal' | 'contact';

export interface AddressBookPage {
  items: readonly PersonEntry[];
  total: number;
  nextCursor: string | null;
}

export interface AddressBookSource {
  load: (query: string, filter: AddressBookFilter, cursor: string | undefined, signal: AbortSignal) => Promise<AddressBookPage>;
  photoUrl?: (email: string) => string | undefined;
}

export interface AddressBookField {
  id: string;
  label: string;
}

export interface AddressBookLabels {
  title: string;
  description: string;
  search: string;
  filters: Record<AddressBookFilter, string>;
  sources: Record<RecipientSource, string>;
  group: string;
  results: string;
  empty: string;
  loadMore: string;
  /** Button text that adds the ticked entries to a field, contains `{field}` (e.g. "To →"). */
  addTo: string;
  /** Contains `{name}`. */
  remove: string;
  apply: string;
  cancel: string;
}

export interface AddressBookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: AddressBookSource;
  fields: readonly AddressBookField[];
  /** Field that a double-click adds to (the field whose button opened the dialog). */
  initialField: string;
  onApply: (selection: Record<string, PersonEntry[]>) => void;
  labels: AddressBookLabels;
}

const DEBOUNCE_MS = 150;

export function AddressBookDialog({ open, onOpenChange, source, fields, initialField, onApply, labels }: AddressBookDialogProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<AddressBookFilter>('all');
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [selection, setSelection] = useState<Record<string, PersonEntry[]>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  // Pages load by cursor as the list is scrolled; earlier results stay while a new search is fetched.
  const list = useInfiniteList<PersonEntry>({
    queryKey: `${query.trim()}\n${filter}`,
    load: async (position, signal) => {
      const page = await source.load(query.trim(), filter, position.cursor, signal);
      return { items: page.items, nextCursor: page.nextCursor };
    },
    getId: (entry) => entry.key,
    enabled: open,
    debounceMs: DEBOUNCE_MS,
  });
  const { items, loadingMore } = list;
  const failed = list.error !== undefined && items.length === 0;
  const loading = open && list.loading;

  const toggle = (key: string) =>
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const addTo = (fieldId: string, entries: readonly PersonEntry[]) => {
    if (entries.length === 0) return;
    setSelection((current) => {
      const list = current[fieldId] ?? [];
      const known = new Set(list.map((e) => e.key));
      return { ...current, [fieldId]: [...list, ...entries.filter((e) => !known.has(e.key))] };
    });
    setChecked(new Set());
  };

  const reset = () => {
    setQuery('');
    setFilter('all');
    setChecked(new Set());
    setSelection({});
  };

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent framed bodySize="default" size="lg">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.description}</DialogDescription>
        </DialogHeader>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as AddressBookFilter)}>
          <TabsList>
            {(['all', 'gal', 'contact'] as const).map((f) => (
              <TabsTrigger key={f} value={f}>
                {labels.filters[f]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <DialogBody ref={scrollRef} className="space-y-3">
          <div className="relative">
            <SearchIcon aria-hidden className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input type="search" autoFocus value={query} onChange={(event) => setQuery(event.target.value)} aria-label={labels.search} placeholder={labels.search} className="pl-8" />
          </div>
          <div className="flex flex-wrap gap-2">{fields.map(field => <Button key={field.id} variant="outline" size="sm" disabled={checked.size === 0} onClick={() => addTo(field.id, items.filter(entry => checked.has(entry.key)))}>{labels.addTo.replace('{field}', field.label)}</Button>)}</div>

          <div className="flex flex-col gap-2">
            {fields.map((f) => {
              const picked = selection[f.id] ?? [];
              if (picked.length === 0) return null;
              return (
                <div key={f.id} className="flex items-start gap-2">
                  <span className="w-20 shrink-0 text-sm">{f.label}</span>
                  <ul aria-label={f.label} className="flex min-h-control-sm flex-1 flex-wrap items-center gap-1 rounded-md border px-1.5 py-0.5">
                    {picked.map((e) => (
                      <li key={e.key} className="inline-flex items-center gap-1 rounded-full border bg-secondary py-0.5 pr-0.5 pl-0.5 text-xs">
                        <PersonAvatar name={e.name} email={e.email} group={e.kind === 'group'} size="xs" />
                        <span className="max-w-48 truncate">{e.name || e.email}</span>
                        <button
                          type="button"
                          aria-label={labels.remove.replace('{name}', e.name || e.email)}
                          onClick={() => setSelection((current) => ({ ...current, [f.id]: (current[f.id] ?? []).filter((x) => x.key !== e.key) }))}
                          className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <XIcon className="size-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="rounded-md border">
            {failed ? (
              <ErrorState onRetry={list.reload} />
            ) : loading && items.length === 0 ? (
              <div className="flex flex-col gap-3 p-3" aria-busy="true">
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} className="h-6 w-2/3" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <EmptyState title={labels.empty} />
            ) : (
              <ul role="listbox" aria-multiselectable="true" aria-label={labels.results} className="divide-y">
                {items.map((entry) => {
                  const isChecked = checked.has(entry.key);
                  return (
                    <li
                      key={entry.key}
                      role="option"
                      aria-selected={isChecked}
                      onDoubleClick={() => addTo(initialField, [entry])}
                      className={cn('flex items-center gap-3 px-3 py-1.5 hover:bg-muted/60', isChecked && 'bg-accent')}
                    >
                      <Checkbox checked={isChecked} onCheckedChange={() => toggle(entry.key)} aria-label={entry.name || entry.email} />
                      <PersonAvatar name={entry.name} email={entry.email} group={entry.kind === 'group'} photoUrl={source.photoUrl?.(entry.email)} />
                      <button type="button" tabIndex={-1} onClick={() => toggle(entry.key)} className="flex min-w-0 flex-1 flex-col text-left">
                        <span className="truncate text-sm font-medium">{entry.name || entry.email}</span>
                        <span className="truncate text-xs text-muted-foreground">{entry.email}</span>
                      </button>
                      {entry.kind === 'group' && <Badge variant="muted">{labels.group}</Badge>}
                      <Badge variant="outline">{labels.sources[entry.source]}</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
            <InfiniteScrollSentinel rootRef={scrollRef} onVisible={list.loadMore} disabled={!list.hasMore || loadingMore || list.error !== undefined} watch={items.length}>
              {list.hasMore && (
                <div className="flex justify-center p-2">
                  <Button variant="ghost" size="sm" disabled={loadingMore} onClick={list.loadMore}>
                    {labels.loadMore}
                  </Button>
                </div>
              )}
            </InfiniteScrollSentinel>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            {labels.cancel}
          </Button>
          <Button
            onClick={() => {
              onApply(selection);
              close(false);
            }}
          >
            {labels.apply}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
